import type { ComponentProps } from 'react';
import React, { useCallback, useMemo } from 'react';

import { Button } from '@change/ds-core-ui';
import { cn } from '@change/ds-utils';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { FacebookPostSheet, PetitionShareSlideUp } from '#src/app/pages/psf/promoteOrShare/shared/components';
import type { FacebookPostSheetTap } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import { useShareActions } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import type { FacebookGroupRecommendation } from '#src/app/pages/psf/shared/api';
import { ShareEducationModalProvider, useShareEducationModal } from '#src/app/shared/hooks/share';

export type ButtonVariant = ComponentProps<typeof Button>['variant'];

type ShareActionsData = ReturnType<typeof useShareActions>['data'];

/**
 * EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment.
 * The post sheet's handlers. The feed CTA and the `variant_2` group CTA both hand the page the Facebook tap that opened the sheet, so the success event carries
 * that tap's `share_placement`. Each then closes the sheet without a dismissal.
 */
function useFacebookPostSheetHandlers(
	tap: FacebookPostSheetTap | null,
	closeSheet: () => void,
	{
		facebookPostSheet,
		facebookPostSheetArm,
		facebookPostSheetGroups,
	}: Pick<ShareActionsData, 'facebookPostSheet' | 'facebookPostSheetArm' | 'facebookPostSheetGroups'>,
) {
	const { postToFeed, postToGroup, selectGroup, trackGroupCardViewed, trackDismissed } = facebookPostSheet;

	const onPost = useCallback(() => {
		if (tap) postToFeed(tap);
		closeSheet();
	}, [tap, postToFeed, closeSheet]);

	const onPostToGroup = useCallback(
		(group: FacebookGroupRecommendation) => {
			if (tap) postToGroup(tap, group);
			closeSheet();
		},
		[tap, postToGroup, closeSheet],
	);

	const onDismiss = useCallback(() => {
		trackDismissed();
		closeSheet();
	}, [trackDismissed, closeSheet]);

	// `variant_2` only: the other arms open the single-step feed sheet.
	const groupDestination = useMemo(
		() =>
			facebookPostSheetArm === 'destination_choice'
				? {
						groups: facebookPostSheetGroups,
						onSelectGroup: selectGroup,
						onPostToGroup,
						onGroupCardViewed: trackGroupCardViewed,
					}
				: undefined,
		[facebookPostSheetArm, facebookPostSheetGroups, selectGroup, onPostToGroup, trackGroupCardViewed],
	);

	return { onPost, onDismiss, groupDestination };
}

type ShareSlideUpProps = {
	shareButtonLabel: string;
	buttonVariant?: ButtonVariant;
	buttonClassName?: string;
};

export const ShareSlideUp: React.FC<ShareSlideUpProps> = ({
	shareButtonLabel,
	buttonVariant = 'secondary',
	buttonClassName,
}) => {
	const [isSlideUpOpen, setSlideUpOpen] = React.useState(false);
	// EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment.
	// The Facebook tap that opened the post sheet, or `null` while it is closed. Lives here rather than in
	// the slide-up because the sheet has to outlive it: the slide-up closes on the Facebook tap.
	const [facebookPostSheetTap, setFacebookPostSheetTap] = React.useState<FacebookPostSheetTap | null>(null);

	const {
		data: shareActionsData,
		actions: { handleOpenShareSlideUp, handleCloseShareSlideUp },
	} = useShareActions({ onOpenFacebookPostSheet: setFacebookPostSheetTap });
	const {
		buttonConfigs,
		continueButtonProps,
		shareVisualUrl,
		channelAwareCta,
		facebookPostSheet,
		qrCodeLayout,
		qrCode,
		qrCodeTracking,
		qrCodePoster,
		onQrCodeShared,
		cancelQrRedirect,
	} = shareActionsData;

	const {
		data: { petition },
	} = usePsfPromoteOrSharePageData();

	const handleOpen = useCallback(() => {
		handleOpenShareSlideUp();
		setSlideUpOpen(true);
	}, [handleOpenShareSlideUp]);

	/**
	 * Single modal instance shared across all channel share actions (instagram,
	 * instagramStories, tiktok).  Channel hooks read openModal from context and
	 * swap in their own content at open-time.
	 */
	const [openEducationModal, educationModal] = useShareEducationModal();

	const { resetCopy: resetFacebookPostCopy } = facebookPostSheet;
	const closeFacebookPostSheet = useCallback(() => {
		resetFacebookPostCopy();
		setFacebookPostSheetTap(null);
	}, [resetFacebookPostCopy]);
	const facebookPostSheetHandlers = useFacebookPostSheetHandlers(
		facebookPostSheetTap,
		closeFacebookPostSheet,
		shareActionsData,
	);

	return (
		<ShareEducationModalProvider value={openEducationModal}>
			<div className="w-full md:hidden">
				<Button
					className={cn('mt-2 w-full [&>div]:leading-5', { 'py-3': buttonVariant === 'tertiary' }, buttonClassName)}
					variant={buttonVariant}
					data-qa="share-button-slide-up"
					data-testid="share-button-slide-up"
					onClick={handleOpen}
				>
					{shareButtonLabel}
				</Button>
			</div>

			<PetitionShareSlideUp
				petition={{
					ask: petition.ask,
					description: petition.description,
					photo: petition.photo?.petitionSmall.url,
				}}
				buttonConfigs={buttonConfigs}
				channelAwareCta={channelAwareCta}
				qrCodeLayout={qrCodeLayout}
				qrCode={qrCode}
				qrCodeTracking={qrCodeTracking}
				qrCodePoster={qrCodePoster}
				onQrCodeShared={onQrCodeShared}
				cancelQrRedirect={cancelQrRedirect}
				shareVisual={shareVisualUrl}
				isOpen={isSlideUpOpen}
				onOpenChange={(open: boolean) => {
					if (!open) {
						handleCloseShareSlideUp();
						setSlideUpOpen(false);
					}
				}}
				continueButtonProps={continueButtonProps}
			/>
			{educationModal}
			{facebookPostSheet.caption !== null && (
				<FacebookPostSheet
					isOpen={facebookPostSheetTap !== null}
					caption={facebookPostSheet.caption}
					petition={{ ask: petition.ask, photo: petition.photo?.petitionSmall.url }}
					copied={facebookPostSheetTap?.copied === true}
					onPost={facebookPostSheetHandlers.onPost}
					onDismiss={facebookPostSheetHandlers.onDismiss}
					onView={facebookPostSheet.trackViewed}
					groupDestination={facebookPostSheetHandlers.groupDestination}
				/>
			)}
		</ShareEducationModalProvider>
	);
};
